document.addEventListener("DOMContentLoaded", function () {
  const root = document.querySelector(".firearm-options-container");
  if (!root || !window.nffi) return;

  const brandSelect = root.querySelector("#firearm-brand");
  const modelSelect = root.querySelector("#firearm-model");
  const holsterColorSelect = root.querySelector("#holster-color");
  const holstersContainer = root.querySelector("#holsters-container");
  const selectedHolsterIdInput = root.querySelector("#selected-holster-id");
  const selectedHolsterSKUInput = root.querySelector("#selected-holster-sku");
  
  // -----------------------------
  // Selected labels near dropdown title
  // -----------------------------
  function initDropdownSelectedLabels(scope = document) {
    const wraps = Array.from(scope.querySelectorAll(".firearm-dropdowns"));

    wraps.forEach((wrap) => {
      const label = wrap.querySelector(".dropdown-label");
      const select = wrap.querySelector("select.dropdown-select");
      if (!label || !select) return;

      // Save base label text once (without trailing ": ...")
      if (!label.dataset.baseText) {
        label.dataset.baseText = (label.textContent || "")
          .trim()
          .replace(/:.*$/, "");
      }

      const baseText = label.dataset.baseText;

      const update = () => {
        const opt = select.options?.[select.selectedIndex];
        const selectedText = (opt?.textContent || "").trim();

        const isPlaceholder =
          !select.value ||
          select.value === "" ||
          (opt && opt.disabled) ||
          /^select\b/i.test(selectedText);

        label.textContent = isPlaceholder ? baseText : `${baseText}: ${selectedText}`;
      };

      update();
      select.removeEventListener("change", update); // safety
      select.addEventListener("change", update);
    });
  }

  // Export for bag-options.js to call after show/hide
  window.NFFI_FirearmOptions = window.NFFI_FirearmOptions || {};
  window.NFFI_FirearmOptions.initDropdownSelectedLabels = initDropdownSelectedLabels;

  // -----------------------------
  // Load JSON data
  // -----------------------------
    
  initializeFirearmDataFromManifest(root);


    window.nffi.firearm_brands = window.nffi.firearm_brands || [];
    window.nffi.firearm_models = window.nffi.firearm_models || [];

    /**
     * Fetch JSON from Shopify Files.
     * Throws an error if the URL is empty, the HTTP response is not successful,
     * or the response body is not valid JSON.
     */
    async function fetchFirearmJsonFromShopifyFiles(url, contextLabel) {
      if (!url) {
        throw new Error(`${contextLabel}: URL is empty.`);
      }

      const response = await fetch(url, { cache: "no-store" });

      if (!response.ok) {
        throw new Error(
          `${contextLabel}: request failed with status ${response.status}.`
        );
      }

      return await response.json();
    }

    /**
     * Fetch JSON from the current file first.
     * If that fails, try the fallback file.
     */
    async function fetchFirearmJsonWithFallbackFromShopifyFiles(
      currentUrl,
      fallbackUrl,
      contextLabel
    ) {
      try {
        return await fetchFirearmJsonFromShopifyFiles(
          currentUrl,
          `${contextLabel} (current)`
        );
      } catch (currentError) {
        console.warn(
          `${contextLabel}: failed to load current file, trying fallback.`,
          currentError
        );

        if (!fallbackUrl) {
          throw currentError;
        }

        return await fetchFirearmJsonFromShopifyFiles(
          fallbackUrl,
          `${contextLabel} (fallback)`
        );
      }
    }

    /**
     * Resolve the correct firearm models manifest entry for the current product.
     */
    function resolveFirearmModelsManifestEntryForProduct(manifest) {
      const productTagsCsv = (window.nffi.productTagsCsv || "").toLowerCase();
      const bagSizeCode = normalizeFirearmBagSizeCode(window.nffi.bagSizeCode);

      const isBagProduct =
        productTagsCsv.includes(",bags,") ||
        productTagsCsv.includes(",bundle-bag-hlst,") ||
        productTagsCsv.includes(",chest-holster,");

      const isHolsterProduct =
        productTagsCsv.includes(",holsters,") ||
        productTagsCsv.includes(",bundle-hlst,");

      if (isBagProduct) {
        if (bagSizeCode.includes("XL")) {
          return manifest.models?.bagsXlarge;
        }

        if (bagSizeCode.includes("L")) {
          return manifest.models?.bagsLarge;
        }

        if (bagSizeCode.includes("S")) {
          return manifest.models?.bagsSmall;
        }

        throw new Error(
          "Bag size could not be resolved for firearm models manifest entry."
        );
      }

      if (isHolsterProduct) {
        const isVelcro = productTagsCsv.includes("velcro");
        const isIwb = productTagsCsv.includes("iwb");
        const isLightBearing = productTagsCsv.includes(",light bearing,");
        const isRegular = productTagsCsv.includes(",regular,");

        if (isVelcro && isLightBearing) {
          return manifest.models?.holstersVelcroLightBearing;
        }

        if (isVelcro && isRegular) {
          return manifest.models?.holstersVelcroRegular;
        }

        if (isIwb && isLightBearing) {
          return manifest.models?.holstersIwbLightBearing;
        }

        if (isIwb && isRegular) {
          return manifest.models?.holstersIwbRegular;
        }

        throw new Error(
          "Holster type could not be resolved for firearm models manifest entry."
        );
      }

      throw new Error(
        "Firearm models manifest entry could not be resolved for the current product."
      );
    }

    /**
     * Show a graceful error state instead of leaving the UI half-broken.
     */
    function handleFirearmDataInitializationError(rootElement, error) {
      const errorContainer = rootElement.querySelector("[data-firearm-data-error]");
      if (errorContainer) {
        errorContainer.textContent =
          "Firearm model data is temporarily unavailable. Please try again later.";
        errorContainer.style.display = "block";
      }

      const selectorsContainer = rootElement.querySelector(
        "[data-firearm-selectors]"
      );
      if (selectorsContainer) {
        selectorsContainer.style.display = "none";
      }

      console.error("Failed to initialize firearm data from manifest:", error);
    }

    /**
     * Validate the minimal manifest structure required by the storefront.
     */
    function validateFirearmManifestStructure(manifest) {
      if (!manifest || typeof manifest !== "object") {
        throw new Error("Firearm manifest is missing or invalid.");
      }

      if (!manifest.brands || !manifest.models) {
        throw new Error("Firearm manifest does not contain required sections.");
      }

      if (!manifest.brands.currentUrl && !manifest.brands.fallbackUrl) {
        throw new Error("Firearm brands manifest entry does not contain usable URLs.");
      }
    }

    /**
     * Initialize firearm brands and models from the Shopify manifest file.
     */
    async function initializeFirearmDataFromManifest(rootElement) {
      try {
        const firearmManifest = await fetchFirearmJsonFromShopifyFiles(
          window.nffi.firearmManifestJsonUrl,
          "firearm manifest"
        );

        validateFirearmManifestStructure(firearmManifest);

        const firearmModelsManifestEntry =
          resolveFirearmModelsManifestEntryForProduct(firearmManifest);

        if (
          !firearmModelsManifestEntry ||
          (!firearmModelsManifestEntry.currentUrl &&
            !firearmModelsManifestEntry.fallbackUrl)
        ) {
          throw new Error(
            "Firearm models manifest entry does not contain usable URLs."
          );
        }

        const [firearmBrands, firearmModels] = await Promise.all([
          fetchFirearmJsonWithFallbackFromShopifyFiles(
            firearmManifest.brands.currentUrl,
            firearmManifest.brands.fallbackUrl,
            "firearm brands"
          ),
          fetchFirearmJsonWithFallbackFromShopifyFiles(
            firearmModelsManifestEntry.currentUrl,
            firearmModelsManifestEntry.fallbackUrl,
            "firearm models"
          ),
        ]);

        window.nffi.firearm_brands = firearmBrands;
        window.nffi.firearm_models = firearmModels;

        populateBrands();
        initDropdownSelectedLabels(rootElement);
      } catch (error) {
        window.nffi.firearm_brands = [];
        window.nffi.firearm_models = [];

        handleFirearmDataInitializationError(rootElement, error);
      }
    }

    function normalizeFirearmBagSizeCode(rawBagSizeCode) {
      if (typeof rawBagSizeCode === "string") {
        return rawBagSizeCode.toUpperCase();
      }

      if (Array.isArray(rawBagSizeCode)) {
        const firstValue = rawBagSizeCode.find(
          (item) => typeof item === "string" && item.trim() !== ""
        );

        return firstValue ? firstValue.toUpperCase() : "";
      }

      if (rawBagSizeCode == null) {
        return "";
      }

      return String(rawBagSizeCode).toUpperCase();
    }

  function populateBrands() {
    if (!brandSelect) return;

    const brandIdsWithModels = new Set(window.nffi.firearm_models.map((m) => m.brandId));
    window.nffi.firearm_brands
      .filter((brand) => brandIdsWithModels.has(brand.id))
      .forEach((brand) => {
        const option = document.createElement("option");
        option.value = brand.id;
        option.textContent = brand.name;
        brandSelect.appendChild(option);
      });

    initDropdownSelectedLabels(root);
  }

  function populateModels(brandId) {
    if (!modelSelect) return;

    modelSelect.innerHTML = '<option value="" disabled selected>Select Model</option>';

    const filtered = window.nffi.firearm_models.filter(
      (m) => m.brandId.toString() === brandId.toString()
    );

    filtered.forEach((model) => {
      const option = document.createElement("option");
      option.value = model.sku; // NOTE: SKU in value, text is Name
      option.textContent = model.name;
      modelSelect.appendChild(option);
    });

    modelSelect.disabled = filtered.length === 0;
    initDropdownSelectedLabels(root);
  }

  // -----------------------------
  // Brand change
  // -----------------------------
  if (brandSelect) {
    brandSelect.addEventListener("change", function () {
      if (holsterColorSelect) holsterColorSelect.selectedIndex = 0;

      if (!this.value) {
        if (modelSelect) {
          modelSelect.disabled = true;
          modelSelect.innerHTML = '<option value="" disabled selected>Select Model</option>';
        }
        if (holstersContainer) holstersContainer.style.display = "none";
      } else {
        populateModels(this.value);

        if (holstersContainer && holsterColorSelect) {
          holsterColorSelect.disabled = true;
          holstersContainer.style.display = "none";
        }
      }

      initDropdownSelectedLabels(root);

      document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
    });
  }

  // -----------------------------
  // Model change
  // -----------------------------
  if (modelSelect && holsterColorSelect) {
    modelSelect.addEventListener("change", function () {
      const selectedModel = this.value;
      holsterColorSelect.disabled = !selectedModel;
      holsterColorSelect.selectedIndex = 0;

      initDropdownSelectedLabels(root);

      // Clear holster card on model change
      if (holstersContainer) {
        holstersContainer.innerHTML = "";
        holstersContainer.style.display = "none";
      }

      // Also clear hidden selected holster (prevents stale selection)
      if (selectedHolsterIdInput) selectedHolsterIdInput.value = "";
      if (selectedHolsterSKUInput) selectedHolsterSKUInput.value = "";

      // Trigger color change to refresh (keeps your behavior)
      holsterColorSelect.dispatchEvent(new Event("change"));

      document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
    });
  }

  // -----------------------------
  // Holster color change
  // -----------------------------
  if (holsterColorSelect && holstersContainer) {
    holsterColorSelect.addEventListener("change", function () {
      const selectedColor = this.value;
      holstersContainer.innerHTML = "";

      // Always update the label "Holster color: X"
      initDropdownSelectedLabels(root);

      if (!selectedColor) {
        holstersContainer.style.display = "none";
        if (selectedHolsterIdInput) selectedHolsterIdInput.value = "";
        if (selectedHolsterSKUInput) selectedHolsterSKUInput.value = "";        
        return;
      }

      const modelVal = modelSelect?.value || "";
      const isLightBearing = modelVal.includes("+");
      const selectedColorLower = selectedColor.toLowerCase().replaceAll(" ", "-");
      const desiredTag = isLightBearing ? "LIGHT BEARING" : "REGULAR";

      const holsters = window.nffi?.product_addons?.Holsters || [];
      const selectedHolster = holsters.find((h) => {
        const handleLower = (h.handle || "").toLowerCase();
        const tags = Array.isArray(h.tags)
          ? h.tags.map((t) => String(t).toUpperCase())
          : String(h.tags || "").toUpperCase();

        const matchesTag = tags.includes(desiredTag);
        const matchesColor = handleLower.includes(selectedColorLower);

        return matchesTag && matchesColor;
      });

      if (selectedHolster) {
        renderHolster(selectedHolster);
      } else {
        holstersContainer.style.display = "none";
      }

      if (selectedHolsterIdInput) {
        selectedHolsterIdInput.value = selectedHolster?.variants?.[0]?.id || "";
      }
      if (selectedHolsterSKUInput) {
        selectedHolsterSKUInput.value = selectedHolster?.variants?.[0]?.sku || "";
      }

      const holsterPriceInput = document.querySelector(".firearm-options-container #selected-holster-price-cents");
      if (holsterPriceInput) {
        holsterPriceInput.value = selectedHolster ? String(selectedHolster.price || 0) : "";
      }

      const selectedHolsterNameInput = root.querySelector(".firearm-options-container #selected-holster-name");
      if(selectedHolsterNameInput){
        selectedHolsterNameInput.value = selectedHolster ? selectedHolster.title : "";
      }
console.log('[selectedHolster]', selectedHolster);
      // notify other scripts that holster selection changed
      document.dispatchEvent(new CustomEvent("nffi:holster-selected", selectedHolster));

    });
  }

  function renderHolster(holster) {
    holstersContainer.innerHTML = "";

    const image = holster.images && holster.images.length > 0 ? holster.images[0] : "";

    const productCard = document.createElement("product-card");
    productCard.className = "product-card bg-custom text-custom";
    productCard.setAttribute(
      "style",
      "--background: 224 224 224; --text-color: 26 26 26; opacity: 1; transform: translateY(0px);"
    );

    productCard.innerHTML = `
      <div class="product-card__figure" data-country="USA">
        <img 
          src="${image}" 
          alt="${String(holster.title || "").toLowerCase()}" 
          width="2500" height="2500"
          class="product-card__image product-card__image--primary aspect-square"
          loading="lazy"
        >
      </div>
      <div class="product-card__info product-card__info--center">
        <div class="v-stack gap-0.5 w-full justify-items-center">
          <span class="product-card__title">${holster.title || ""}</span>
          <price-list class="price-list justify-center">
            <sale-price class="bcpo-cart-item-pricetext-subdued">
              <span class="sr-only">Sale price</span>$${(parseFloat(holster.price || 0) / 100).toFixed(2)}
            </sale-price>
          </price-list>
        </div>
      </div>
    `;

    holstersContainer.appendChild(productCard);
    holstersContainer.style.display = "block";
  }
});
